import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCostGroupsData, deleteCostGroup } from '../../store/slices/settingsSlice';
import LoadingSpinner from '../common/LoadingSpinner';
import AddEditCostGroupDialog from './AddEditCostGroupDialog';
import ConfirmDialog from '../common/ConfirmDialog';

const CostGroupsTab = () => {
  const dispatch = useDispatch();
  const { costGroupsData, loading, pagination } = useSelector(state => state.settings);
  const { user: currentUser } = useSelector(state => state.auth);
  const [page, setPage] = useState(1);
  const [dialogMode, setDialogMode] = useState({ type: null, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortedGroups, setSortedGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 10;

  const userRole = currentUser?.profile?.role;
  const hasEditPermission = ['super_admin', 'admin'].includes(userRole);

  console.log('CostGroupsTab Permissions:', {
    userRole,
    hasEditPermission,
    currentUser
  });

  useEffect(() => {
    dispatch(fetchCostGroupsData({ page, limit: pageSize }));
  }, [dispatch, page]);

  useEffect(() => {
    // Sort and filter cost groups when data, sort order, or search query changes
    let filteredGroups = [...costGroupsData];
    
    // Apply search filter if there's a query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredGroups = filteredGroups.filter(group => 
        group.groupId.toLowerCase().includes(query) || 
        group.title.toLowerCase().includes(query) || 
        group.description?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    filteredGroups.sort((a, b) => {
      const titleA = a.title.toLowerCase();
      const titleB = b.title.toLowerCase();
      
      if (sortOrder === 'asc') {
        return titleA.localeCompare(titleB);
      } else {
        return titleB.localeCompare(titleA);
      }
    });
    
    setSortedGroups(filteredGroups);
  }, [costGroupsData, sortOrder, searchQuery]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleAdd = () => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'add', data: null });
  };

  const handleEdit = (costGroup) => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'edit', data: costGroup });
  };

  const handleDelete = (costGroup) => {
    if (!hasEditPermission) return;
    setDeleteConfirm({ show: true, item: costGroup });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.item) {
      await dispatch(deleteCostGroup(deleteConfirm.item._id));
      setDeleteConfirm({ show: false, item: null });
      dispatch(fetchCostGroupsData({ page, limit: pageSize }));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-medium">Cost Groups ({pagination?.total || 0})</h3>
        {hasEditPermission && (
          <button 
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Cost Group
          </button>
        )}
      </div>

      {/* Search input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search cost groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group ID
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={toggleSortOrder}
                  >
                    <div className="flex items-center">
                      Title
                      <span className="ml-1">
                        {sortOrder === 'asc' ? '▲' : '▼'}
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedGroups.map((group) => (
                  <tr key={group._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {group.groupId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {group.title}
                    </td>
                    <td className="px-6 py-4">
                      {group.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hasEditPermission ? (
                        <div className="flex space-x-4">
                          <button
                            onClick={() => handleEdit(group)}
                            className="text-blue-600 hover:text-blue-900 cursor-pointer"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(group)}
                            className="text-red-600 hover:text-red-900 cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No actions available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {pagination?.pages > 1 && (
        <div className="flex justify-center mt-4">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
              Page {page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.pages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {hasEditPermission && (
        <>
          <AddEditCostGroupDialog
            isOpen={!!dialogMode.type}
            mode={dialogMode.type}
            data={dialogMode.data}
            onClose={() => setDialogMode({ type: null, data: null })}
            onSuccess={() => {
              setDialogMode({ type: null, data: null });
              dispatch(fetchCostGroupsData({ page, limit: pageSize }));
            }}
          />

          <ConfirmDialog
            isOpen={deleteConfirm.show}
            onClose={() => setDeleteConfirm({ show: false, item: null })}
            onConfirm={handleConfirmDelete}
            title="Delete Cost Group"
            message={`Are you sure you want to delete "${deleteConfirm.item?.title}"? This action cannot be undone.`}
          />
        </>
      )}
    </div>
  );
};

export default CostGroupsTab; 